import React, { useState, useEffect, useCallback } from "react"

import * as moment from "moment"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"

import { useDataProvider } from "react-admin"
import Rank from "./Rank"
import Loading from "../../Layout/Loading"
import {
  Container,
  HeaderRow,
  Title,
  PeriodSelect,
  SummaryGrid,
  SummaryCard,
  SummaryTop,
  IconWrapper,
  SummaryLabel,
  SummaryValue,
  MainSection,
  ChartCard,
  RankCard,
} from "./styles"

const Statistics = (props) => {
  const dataProvider = useDataProvider()

  const [dataChart, setDataChart] = useState([])
  const [periodSelect, setPeriodSelect] = useState("day")
  const [rank, setRank] = useState([])
  const [loading, setLoading] = useState(true)

  const [data, setData] = useState({
    orders: 50,
    views: 100,
    sells: 5000,
    qrcodeViews: 70,
  })

  const getTotalizers = useCallback(
    async (startDate) => {
      try {
        const { data } = await dataProvider.getStatistics("totalizers", {
          startDate: startDate,
          endDate: moment().format(),
          events: [
            "view_menu",
            "view_menu_qrcode",
            "click_whatsapp",
            "click_facebook",
            "click_instagram",
            "order",
          ],
        })
        return data
      } catch (error) {
        console.log(error)
      }
    },
    [dataProvider]
  )

  const getChart = useCallback(
    async (startDate, groupBy) => {
      try {
        const { data } = await dataProvider.getStatistics("chart", {
          startDate: startDate,
          groupBy,
          endDate: moment().format("YYYY-MM-DD 23:59:00"),
          events: ["view_menu", "order"],
        })

        return data
      } catch (error) {
        console.log(error)
      }
    },
    [dataProvider]
  )
  const getMostSeen = useCallback(
    async (startDate) => {
      try {
        const { data } = await dataProvider.getStatistics("mostSeen", {
          startDate: startDate,
          endDate: moment().format("YYYY-MM-DD 23:59:00"),
          events: ["view_product", "add_to_cart", "buy_product"],
        })

        const refs = [
          ...data.view_product?.map((i) => i.referenceId),
          ...data.add_to_cart?.map((i) => i.referenceId),
          ...data.buy_product?.map((i) => i.referenceId),
        ]
        const products = await dataProvider.getMany("product", { ids: refs })
        return {
          view_product:
            data?.view_product?.map((i) => {
              const prod = products.data.find((f) => f.id === i.referenceId)
              console.log(prod)
              return {
                product: prod.name,
                image: prod?.photos[0]?.thumbnailUrl,
                total: i.total,
              }
            }) ?? [],
          add_to_cart:
            data?.add_to_cart?.map((i) => {
              const prod = products.data.find((f) => f.id === i.referenceId)
              console.log(prod)
              return {
                product: prod.name,
                image: prod?.photos[0]?.thumbnailUrl,
                total: i.total,
              }
            }) ?? [],
          buy_product:
            data?.buy_product?.map((i) => {
              const prod = products.data.find((f) => f.id === i.referenceId)
              console.log(prod)
              return {
                product: prod.name,
                image: prod?.photos[0]?.thumbnailUrl,
                total: i.total,
              }
            }) ?? [],
        }
      } catch (error) {
        console.log(error)
      }
    },
    [dataProvider]
  )

  const loadData = useCallback(async () => {
    let startDate = ""
    setLoading(true)

    switch (periodSelect) {
      case "day":
        {
          startDate = moment().format("YYYY-MM-DD")
          const data = await getTotalizers(startDate)
          const dataChart = await getChart(startDate, "hour")
          const rank = await getMostSeen(startDate)
          setData(data)
          setDataChart(dataChart)
          setRank(rank)
        }
        break
      case "yesterday":
        {
          startDate = moment().subtract(1, "days").format("YYYY-MM-DD")
          const data = await getTotalizers(startDate)
          const dataChart = await getChart(startDate, "day")
          const rank = await getMostSeen(startDate)
          setData(data)
          setDataChart(dataChart)
          setRank(rank)
        }
        break
      case "week":
        {
          startDate = moment().subtract(7, "days").format()
          const data = await getTotalizers(startDate)
          const dataChart = await getChart(startDate, "day")
          const rank = await getMostSeen(startDate)
          setData(data)
          setDataChart(dataChart)
          setRank(rank)
        }

        break
      case "month":
        {
          startDate = moment().subtract(1, "month").format()
          const data = await getTotalizers(startDate)
          const dataChart = await getChart(startDate, "month")
          const rank = await getMostSeen(startDate)
          setData(data)
          setDataChart(dataChart)
          setRank(rank)
        }

        break
      case "year":
        {
          startDate = moment().subtract(1, "year").format()
          const data = await getTotalizers(startDate)
          const dataChart = await getChart(startDate, "month")
          const rank = await getMostSeen(startDate)
          setData(data)
          setDataChart(dataChart)
          setRank(rank)
        }
        break
      default:
        break
    }
    setLoading(false)
  }, [periodSelect, getTotalizers, getChart, getMostSeen])

  useEffect(() => {
    loadData()
  }, [loadData])

  const summary = [
    {
      label: "Acessos",
      value: data?.view_menu ?? 0,
      icon: require("../../Assets/images/icon_view.png"),
      bg: "#ece8ff",
    },
    {
      label: "Pedidos",
      value: data?.order ?? 0,
      icon: require("../../Assets/images/icon_order.png"),
      bg: "#def7e6",
    },
    {
      label: "Redes Sociais",
      value:
        (data?.click_whatsapp ?? 0) +
        (data?.click_facebook ?? 0) +
        (data?.click_instagram ?? 0),
      icon: require("../../Assets/images/icon_socialmedia.png"),
      bg: "#d5ebff",
    },
    {
      label: "QRCode",
      value: data?.view_menu_qrcode ?? 0,
      icon: require("../../Assets/images/icon_qrcode.png"),
      bg: "#ffdfde",
    },
  ]

  return (
    <>
      <Loading show={loading} />
      <Container>
        <HeaderRow>
          <Title>Estatísticas</Title>
          <PeriodSelect
            value={periodSelect}
            onChange={(e) => setPeriodSelect(e.target.value)}
          >
            <option value="day">Hoje</option>
            <option value="yesterday">Ontem</option>
            <option value="week">Últimos 7 dias</option>
            <option value="month">Últimos 30 dias</option>
            <option value="year">Últimos 12 meses</option>
          </PeriodSelect>
        </HeaderRow>

        <SummaryGrid>
          {summary.map(({ label, value, icon, bg }) => (
            <SummaryCard key={label}>
              <SummaryTop>
                <IconWrapper bg={bg}>
                  <img src={icon} alt={label} />
                </IconWrapper>
                <SummaryLabel>{label}</SummaryLabel>
              </SummaryTop>
              <SummaryValue>{value ?? 0}</SummaryValue>
            </SummaryCard>
          ))}
        </SummaryGrid>

        <MainSection>
          <ChartCard>
            <ResponsiveContainer width="100%" aspect={8.0 / 3.0}>
              <LineChart
                data={dataChart}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="view_menu"
                  stroke="#6c63ff"
                  activeDot={{ r: 6 }}
                  strokeWidth={3}
                  name="Acessos"
                />
                <Line
                  type="monotone"
                  dataKey="order"
                  stroke="#32a852"
                  strokeWidth={3}
                  activeDot={{ r: 6 }}
                  name="Pedidos"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <RankCard>
            <Rank rank={rank} />
          </RankCard>
        </MainSection>
      </Container>
    </>
  )
}

export default Statistics
